/* Card controls share confirmed cart state and serialize their mutations.
 * Dawn cartUpdate remains the public contract; Northbound owns ATC availability.
 */
(() => {
  if (customElements.get('arctic-card-stepper')) return;

  const instances = new Set();
  const variants = new Map();
  const CHANGE_DELAY = 250;
  let snapshot = null;
  let snapshotError = false;
  let revision = 0;
  let reading = null;
  let mutationQueue = Promise.resolve();
  let subscribed = false;

  const route = (name) => {
    const configured = window.routes?.[`cart_${name}_url`];
    const base = name === 'get'
      ? window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`
      : configured || `${window.Shopify?.routes?.root || '/'}cart/${name}`;
    return base.endsWith('.js') ? base : `${base}.js`;
  };
  const isCart = (data) => Array.isArray(data?.items) && typeof data.item_count === 'number';
  const stateFor = (id) => {
    if (!variants.has(id)) variants.set(id, { desired: null, busy: false, timer: null, owner: null, error: '', uncertain: false });
    return variants.get(id);
  };
  const linesFor = (id) => snapshot?.items.filter((line) => String(line.variant_id || line.id) === id) || [];
  const quantityFor = (id) => linesFor(id).reduce((sum, line) => sum + line.quantity, 0);
  const needsCart = (id) => {
    const lines = linesFor(id);
    return lines.length > 1 || lines.some((line) =>
      line.selling_plan_allocation || Object.keys(line.properties || {}).length > 0 || !line.key
    );
  };
  const renderAll = () => instances.forEach((instance) => instance.render());
  const acceptCart = (data) => {
    snapshot = data;
    snapshotError = false;
    revision += 1;
    variants.forEach((state) => { state.uncertain = false; });
    renderAll();
    return data;
  };

  function readCart() {
    if (reading) return reading;
    const startedAt = revision;
    const request = (async () => {
      const response = await fetch(route('get'), { headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (!response.ok || !isCart(data)) throw new Error('Invalid cart response');
      if (startedAt !== revision) return null;
      return acceptCart(data);
    })();
    const completed = request.then((data) => {
      if (reading === completed) reading = null;
      return data || snapshot || readCart();
    }, (error) => {
      if (reading === completed) reading = null;
      if (startedAt !== revision) return snapshot || readCart();
      throw error;
    });
    reading = completed;
    return completed;
  }

  function ensureCart() {
    return snapshot ? Promise.resolve(snapshot) : readCart();
  }

  function onExternalUpdate(event) {
    if (event?.source === 'card-stepper') return;
    revision += 1;
    if (isCart(event?.cartData)) {
      acceptCart(event.cartData);
      return;
    }
    snapshot = null;
    renderAll();
    readCart().catch(() => {
      snapshotError = true;
      renderAll();
    });
  }

  function publishCart(data, instance) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'card-stepper',
      productVariantId: instance.variantId,
      cartData: data,
    })).catch((error) => console.error('[card-stepper] cart subscriber failed', error));
  }

  async function mutate(instance, kind, desired) {
    const state = stateFor(instance.variantId);
    let responseData = null;
    let sections = [];
    let attempted = false;
    try {
      await ensureCart();
      if (!instance.isConnected || instance.isBlocked() || needsCart(instance.variantId)) return;
      const lines = linesFor(instance.variantId);
      if (kind === 'change' && !lines.length && desired === 0) return;
      const adding = kind === 'add' || !lines.length;
      sections = instance.sectionsToRefresh(adding);
      const body = adding
        ? { id: Number(instance.variantId), quantity: kind === 'add' ? 1 : desired }
        : { id: lines[0].key, quantity: desired };
      body.sections = sections;
      body.sections_url = window.location.pathname;
      revision += 1;
      const startedAt = revision;
      attempted = true;
      const response = await fetch(route(adding ? 'add' : 'change'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      responseData = await response.json();
      if (!response.ok || responseData.status || (!adding && !isCart(responseData))) {
        throw new Error(instance.dataset.errorMessage);
      }
      let data;
      if (!adding && startedAt === revision) {
        data = acceptCart(responseData);
      } else {
        snapshot = null;
        data = await readCart();
      }
      state.desired = null;
      state.error = '';
      publishCart(data, instance);
      // Our confirmed snapshot advances revision once. Additional changes
      // mean another cart writer can already have rendered newer sections.
      if (revision === startedAt + 1) {
        instance.applySections(responseData, sections, adding);
      } else {
        await instance.refreshSections(responseData, sections, adding);
      }
      instance.announce(instance.message('updatedMessage', quantityFor(instance.variantId)));
    } catch (error) {
      state.desired = null;
      state.error = instance.dataset.errorMessage;
      if (attempted) {
        // A failed response can follow a successful server write. Reconcile;
        // never retry a cart mutation automatically.
        revision += 1;
        snapshot = null;
        try {
          const data = await readCart();
          publishCart(data, instance);
        } catch {
          snapshotError = true;
          state.uncertain = true;
        }
      } else {
        snapshotError = true;
        state.uncertain = true;
      }
    } finally {
      state.desired = null;
      state.busy = false;
      state.owner = null;
      renderAll();
      instance.restoreControlFocus();
    }
  }

  function enqueue(instance, kind, desired) {
    const state = stateFor(instance.variantId);
    if (state.busy) return;
    state.busy = true;
    state.owner = instance;
    state.error = '';
    instance.shouldRestoreFocus = instance.contains(document.activeElement);
    renderAll();
    mutationQueue = mutationQueue.then(() => mutate(instance, kind, desired));
  }

  class ArcticCardStepper extends HTMLElement {
    connectedCallback() {
      if (this.closest('[data-card-reveal-measurement]')) return;
      this.variantId = this.dataset.variantId;
      if (!this.variantId || this.registered) return;
      this.registered = true;
      this.controls = this.querySelector('[data-card-stepper-controls]');
      this.addButton = this.querySelector('[data-card-stepper-add]');
      this.qtyWrap = this.querySelector('[data-card-stepper-qty]');
      this.input = this.querySelector('[data-card-stepper-input]');
      this.decrement = this.querySelector('[data-card-stepper-decrement]');
      this.increment = this.querySelector('[data-card-stepper-increment]');
      this.errorNode = this.querySelector('[data-card-stepper-error]');
      this.retryButton = this.querySelector('[data-card-stepper-retry]');
      this.cartLink = this.querySelector('[data-card-stepper-cart]');
      this.cartQuantity = this.querySelector('[data-card-stepper-cart-quantity]');
      this.statusNode = this.querySelector('[data-card-stepper-status]');
      this.initialQty = Number(this.dataset.qty) || 0;
      this.addButton?.addEventListener('click', this.onAdd);
      this.decrement?.addEventListener('click', this.onDecrement);
      this.increment?.addEventListener('click', this.onIncrement);
      this.input?.addEventListener('change', this.onInput);
      this.retryButton?.addEventListener('click', this.onRetry);
      this.availabilityObserver = new MutationObserver(() => this.render());
      this.availabilityObserver.observe(this.addButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'data-northbound-pricing-state'],
      });
      instances.add(this);
      if (!subscribed && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, onExternalUpdate);
        subscribed = true;
      }
      this.render();
      ensureCart().catch(() => {
        snapshotError = true;
        renderAll();
      });
    }

    disconnectedCallback() {
      if (!this.registered) return;
      this.registered = false;
      instances.delete(this);
      this.availabilityObserver?.disconnect();
      this.addButton?.removeEventListener('click', this.onAdd);
      this.decrement?.removeEventListener('click', this.onDecrement);
      this.increment?.removeEventListener('click', this.onIncrement);
      this.input?.removeEventListener('change', this.onInput);
      this.retryButton?.removeEventListener('click', this.onRetry);
      const state = stateFor(this.variantId);
      if (state.owner === this && !state.busy) {
        clearTimeout(state.timer);
        state.timer = null;
        state.desired = null;
        state.owner = null;
        renderAll();
      }
    }

    isBlocked() {
      return this.dataset.available === 'false' || this.addButton?.disabled ||
        this.addButton?.getAttribute('aria-disabled') === 'true' ||
        this.addButton?.dataset.northboundPricingState === 'unavailable';
    }

    message(key, quantity) {
      return (this.dataset[key] || '').replace(/\[quantity\]/g, String(quantity));
    }

    render() {
      if (!this.registered) return;
      const state = stateFor(this.variantId);
      if (snapshot) this.confirmedQty = quantityFor(this.variantId);
      const confirmed = this.confirmedQty ?? this.initialQty;
      const qty = state.desired === null ? confirmed : state.desired;
      const cartOnly = (this.dataset.available === 'false' && confirmed > 0) || (snapshot && needsCart(this.variantId));
      const uncertain = state.uncertain || snapshotError;
      this.dataset.qty = String(qty);
      this.classList.toggle('is-busy', state.busy);
      this.classList.toggle('is-pending', state.busy);
      this.setAttribute('aria-busy', state.busy ? 'true' : 'false');
      // A disabled fieldset gates local work without overwriting Northbound's
      // disabled/aria-disabled attributes or its captured base availability.
      if (this.controls) this.controls.disabled = state.busy || !snapshot || uncertain || this.isBlocked();
      if (this.addButton) this.addButton.hidden = cartOnly || qty > 0 || (state.busy && confirmed === 0);
      if (this.qtyWrap) this.qtyWrap.hidden = cartOnly || (qty === 0 && !state.busy);
      if (this.input) this.input.value = String(qty);
      if (this.cartLink) this.cartLink.hidden = !cartOnly && !uncertain;
      if (this.cartQuantity) {
        this.cartQuantity.hidden = !cartOnly;
        this.cartQuantity.textContent = this.message('quantityMessage', confirmed);
      }
      if (this.errorNode) {
        this.errorNode.textContent = uncertain ? this.dataset.uncertainMessage : state.error || (cartOnly ? this.dataset.multipleMessage : '');
        this.errorNode.hidden = !this.errorNode.textContent;
      }
      if (this.retryButton) {
        this.retryButton.hidden = !uncertain;
        this.retryButton.disabled = state.busy;
      }
      this.classList.toggle('has-cart-error', Boolean(state.error || uncertain));
    }

    announce(message) {
      if (this.statusNode) this.statusNode.textContent = message;
    }

    onAdd = (event) => {
      event.preventDefault();
      const state = stateFor(this.variantId);
      if (!this.isConnected || this.isBlocked() || state.busy || snapshotError) return;
      if (state.timer !== null) {
        this.changeQuantity(1);
        return;
      }
      enqueue(this, 'add', null);
    };

    changeQuantity(qty) {
      const state = stateFor(this.variantId);
      if (!this.isConnected || this.isBlocked() || state.busy || !snapshot || snapshotError || needsCart(this.variantId)) return;
      if (!Number.isSafeInteger(qty) || qty < 0) {
        clearTimeout(state.timer);
        state.timer = null;
        state.desired = null;
        state.owner = null;
        renderAll();
        return;
      }
      state.desired = qty;
      state.error = '';
      state.owner = this;
      clearTimeout(state.timer);
      renderAll();
      state.timer = setTimeout(() => {
        state.timer = null;
        if (this.isConnected) enqueue(this, 'change', state.desired);
      }, CHANGE_DELAY);
    }

    onDecrement = (event) => {
      event.preventDefault();
      this.changeQuantity(Math.max(0, Number(this.dataset.qty) - 1));
    };
    onIncrement = (event) => {
      event.preventDefault();
      this.changeQuantity(Number(this.dataset.qty) + 1);
    };
    onInput = (event) => this.changeQuantity(event.target.value.trim() === '' ? NaN : Number(event.target.value));
    onRetry = async () => {
      const state = stateFor(this.variantId);
      if (state.busy) return;
      state.busy = true;
      renderAll();
      try {
        const data = await readCart();
        state.error = '';
        publishCart(data, this);
      } catch {
        snapshotError = true;
      } finally {
        state.busy = false;
        renderAll();
      }
    };

    restoreControlFocus() {
      if (!this.shouldRestoreFocus || !this.isConnected) return;
      this.shouldRestoreFocus = false;
      if (document.activeElement !== document.body && !this.contains(document.activeElement)) return;
      const target = !this.retryButton?.hidden ? this.retryButton
        : !this.cartLink?.hidden ? this.cartLink
        : !this.addButton?.hidden ? this.addButton : this.input;
      target?.focus();
    }

    sectionsToRefresh(adding) {
      const drawer = adding && document.querySelector('cart-drawer');
      return drawer ? drawer.getSectionsToRender().map((section) => section.id)
        : ['cart-icon-bubble', 'cart-notification-button'];
    }

    async refreshSections(data, sectionIds, adding) {
      const startedAt = revision;
      try {
        const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`, {
          headers: { Accept: 'application/json' },
        });
        const sections = await response.json();
        if (!response.ok || revision !== startedAt) return;
        this.applySections({ ...data, sections }, sectionIds, adding);
      } catch (error) {
        // Cart state is already confirmed. Never replace newer UI with the
        // original mutation's older sections when this read fails.
        console.error('[card-stepper] section refresh failed', error);
      }
    }

    applySections(data, sectionIds, adding) {
      if (data?.sections) sectionIds.forEach((id) => {
        const html = data.sections[id];
        const target = document.getElementById(id);
        if (!html || !target) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (parsed) target.innerHTML = parsed.innerHTML;
      });
      const drawer = adding && document.querySelector('cart-drawer');
      if (drawer) {
        drawer.setActiveElement(document.activeElement);
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      }
    }
  }

  customElements.define('arctic-card-stepper', ArcticCardStepper);
})();
